import {
  Directive,
  HostListener,
  OnInit,
  DestroyRef,
  ElementRef,
  Host,
  Input,
  ViewContainerRef,
  ComponentRef, OnChanges, SimpleChanges, Injector, OnDestroy
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DxTooltipComponent } from 'devextreme-angular';
import { TooltipPresenterComponent } from './tooltip-presenter.component';

@Directive({
    selector: '[npTooltip]',
    standalone: false
})
export class Tooltip implements OnChanges, OnDestroy {
  private _target!: Element;
  private _activated$ = new BehaviorSubject<boolean>(false);
  private presenterRef?: ComponentRef<TooltipPresenterComponent>;

  @Input({ alias: 'npTooltipTextId' }) text?: string;

  constructor(
    private vcr: ViewContainerRef
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['text']) {
      if (this.text) {
        this.createPresenter();
        this.presenterRef.instance.text = this.text;
      } else {
        this.presenterRef?.destroy();
        this.presenterRef = undefined;
      }
    }
  }

  ngOnDestroy() {
    this.presenterRef?.destroy();
    this.presenterRef = undefined;
  }

  set target(element: Element) {
    this._target = element;

    if (this.presenterRef) {
      this.presenterRef.instance.target = element;
    }
  }

  get target() {
    return this._target;
  }

  get active$(): Observable<boolean> {
    return this._activated$;
  }

  readonly activate = () => {
    this._activated$.next(true);
  }

  readonly deactivate = () => {
    this._activated$.next(false);
  }

  readonly set = (activated: boolean) => {
    this._activated$.next(activated);
  }

  private readonly createPresenter = () => {
    if (!this.presenterRef) {
      const injector = Injector.create({
        providers: [
          { provide: Tooltip, useValue: this }
        ],
        parent: this.vcr.injector
      });

      this.presenterRef = this.vcr.createComponent(TooltipPresenterComponent, { injector });
    }
  }
}

@Directive({
    selector: '[npTooltipTrigger]',
    standalone: false
})
export class TooltipTrigger {
  @HostListener('mouseenter') onEnter() { this.tooltip.activate(); }
  @HostListener('mouseleave') onLeave() { this.tooltip.deactivate(); }

  constructor(private tooltip: Tooltip) {

  }
}

@Directive({
    selector: '[npTooltipTarget]',
    standalone: false
})
export class TooltipTarget implements OnInit {

  constructor(
    @Host() private element: ElementRef<HTMLElement>,
    private tooltip: Tooltip) {
  }

  ngOnInit() {
    this.tooltip.target = this.element.nativeElement;
  }
}

@Directive({
    selector: '[npTooltipDisplay]',
    standalone: false
})
export class TooltipDisplay implements OnInit {

  constructor(
    private destroyRef: DestroyRef,
    private component: DxTooltipComponent,
    private tooltip: Tooltip) {}

  ngOnInit() {
    this.component.target = this.tooltip.target;
    this.component.visibleChange.subscribe((visible: boolean) => {
      this.tooltip.set(visible);
    });

    this.tooltip.active$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(active => {
        this.component.visible = active;
      });
  }
}

